import { type FC } from "react";
import { Check, Home } from "lucide-react";
import { useTranslation } from "react-i18next";

import { DropdownMenuItem } from "@/components/_atoms/dropdown-menu";
import { cn } from "@/lib/cn";

import { useUpdateActiveView } from "../../hooks/api/use-update-active-view";
import { useCurrentViewContext } from "../../hooks/use-current-view-context";
import { DatatableState } from "../../models/datatable-state-model";
import { useDatatableContext } from "../datatable/use-datatable-context";

interface StoredViewListMenuItemBaseViewProps {
  datatableState: DatatableState;
  isActive: boolean;
  onSelect?: () => void;
}

export const StoredViewListMenuItemBaseView: FC<StoredViewListMenuItemBaseViewProps> = ({
  datatableState,
  isActive,
  onSelect,
}) => {
  const { t } = useTranslation("common");
  const { setState: setDatatableState } = useDatatableContext();
  const { currentActiveView } = useCurrentViewContext();
  const { updateActiveView, loading: updateActiveViewLoading } = useUpdateActiveView();

  const handleSelect = async () => {
    if (updateActiveViewLoading) {
      return;
    }

    onSelect?.();

    if (currentActiveView) {
      updateActiveView(
        { ...datatableState, id: currentActiveView.id, storedViewId: null },
        {
          onError: () => {
            // To avoid React 18 unhandled-rejection warnings.
          },
        },
      );

      setDatatableState({ state: datatableState, skipApiPropagation: true });
      return;
    }

    setDatatableState({ state: datatableState });
  };

  return (
    <DropdownMenuItem
      className={cn(
        "group flex cursor-default items-center justify-between rounded-xs px-2 py-1.5 text-xs",
        isActive && "bg-accent",
      )}
      onSelect={handleSelect}
    >
      <div className="flex items-center gap-2">
        <Home className="h-3.5 w-3.5 text-muted-foreground" />
        <span>{t("view.baseView")}</span>
      </div>

      <div className="flex items-center gap-1">
        {isActive && <Check className="h-3.5 w-3.5 text-foreground/60" />}
      </div>
    </DropdownMenuItem>
  );
};
